import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { Button } from '@/components/ui';
import { Icon } from '@/components/Icons';

type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

let updateServiceWorker: UpdateServiceWorker | null = null;
let registrationStarted = false;

/**
 * شريط تحديث PWA.
 *
 * لا نُحدّث الصفحة تلقائيًا: قد تكون هناك كتابات محلية بانتظار المزامنة.
 * التحديث يحدث بقرار المستخدم فقط، ولا ندّعي وجود تحديث تلقائي في Electron.
 */
export function PwaUpdateBanner() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (import.meta.env.DEV || !('serviceWorker' in navigator)) {
      return () => {
        mounted.current = false;
      };
    }
    if (!registrationStarted) {
      registrationStarted = true;
      try {
        updateServiceWorker = registerSW({
          immediate: true,
          onNeedRefresh() {
            if (mounted.current) {
              setNeedRefresh(true);
              setDismissed(false);
            }
          },
          onOfflineReady() {
            if (mounted.current) setOfflineReady(true);
          },
          onRegisterError(error) {
            // لا نعرض تفاصيل تقنية للمستخدم؛ الفشل لا يمنع استخدام التطبيق.
            console.warn('Service worker registration failed', error instanceof Error ? error.message : error);
          },
        });
      } catch {
        // بيئة لا تدعم service worker (مثل Electron عبر file:// أو متصفح قديم)
      }
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  if (dismissed || (!needRefresh && !offlineReady)) return null;

  return (
    <div className="pwa-banner" role="status">
      <span className="pwa-banner-icon">
        <Icon name={needRefresh ? 'refresh' : 'check-circle'} size={18} />
      </span>
      <div className="grow">
        <div className="strong small">
          {needRefresh ? 'يتوفر إصدار جديد من التطبيق' : 'التطبيق جاهز للعمل دون اتصال'}
        </div>
        <div className="tiny muted">
          {needRefresh
            ? 'أكمل أي عمل غير محفوظ ثم حدّث. لن تُفقد البيانات المحلية.'
            : 'تم تخزين واجهة التطبيق فقط. البيانات تحتاج اتصالًا إلا في الوضع المحلي.'}
        </div>
      </div>
      <div className="row">
        {needRefresh ? (
          <Button
            size="sm"
            variant="primary"
            icon="refresh"
            onClick={() => {
              void updateServiceWorker?.(true);
            }}
          >
            تحديث الآن
          </Button>
        ) : (
          <Button size="sm" variant="outline" icon="check" onClick={() => setDismissed(true)}>
            تم
          </Button>
        )}
        <Button size="sm" variant="ghost" icon="close" aria-label="إغلاق" onClick={() => setDismissed(true)} />
      </div>
    </div>
  );
}
